import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useRealtimeWorkflows } from '@/hooks/useRealtimeWorkflows';
import { ArrowLeft, Upload, FileJson, CheckCircle, XCircle, Loader2 } from 'lucide-react';

interface ParsedSummary {
  nodeCount: number;
  classTypes: string[];
  inputCount: number;
}

function isUIFormat(json: unknown): boolean {
  if (!json || typeof json !== 'object') return false;
  const obj = json as Record<string, unknown>;
  return Array.isArray(obj.nodes) && Array.isArray(obj.links);
}

function isAPIFormat(json: unknown): json is Record<string, { class_type: string; inputs: Record<string, unknown> }> {
  if (!json || typeof json !== 'object' || Array.isArray(json)) return false;
  const entries = Object.entries(json as Record<string, unknown>);
  if (entries.length === 0) return false;
  return entries.every(
    ([, node]) =>
      node &&
      typeof node === 'object' &&
      'class_type' in (node as Record<string, unknown>) &&
      'inputs' in (node as Record<string, unknown>),
  );
}

function analyzeClientSide(json: unknown): ParsedSummary {
  if (isUIFormat(json)) {
    const ui = json as { nodes: { type: string; widgets_values?: unknown[] }[] };
    const classTypes = new Set<string>();
    let inputCount = 0;
    for (const node of ui.nodes) {
      classTypes.add(node.type);
      inputCount +=
        node.widgets_values?.filter((v) => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean').length ?? 0;
    }
    return { nodeCount: ui.nodes.length, classTypes: [...classTypes], inputCount };
  }

  const entries = Object.values(json as Record<string, { class_type: string; inputs: Record<string, unknown> }>);
  let inputCount = 0;
  const classTypes = new Set<string>();
  for (const node of entries) {
    classTypes.add(node.class_type);
    for (const value of Object.values(node.inputs)) {
      if (!Array.isArray(value) && (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')) {
        inputCount++;
      }
    }
  }
  return { nodeCount: entries.length, classTypes: [...classTypes], inputCount };
}

function isValidWorkflow(json: unknown): boolean {
  return isUIFormat(json) || isAPIFormat(json);
}

export default function WorkflowNewPage() {
  const navigate = useNavigate();
  const { uploadWorkflow } = useRealtimeWorkflows();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [fileName, setFileName] = useState('');
  const [parsedWorkflow, setParsedWorkflow] = useState<Record<string, unknown> | null>(null);
  const [summary, setSummary] = useState<ParsedSummary | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const processFile = (file: File) => {
    setParseError(null);
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const json = JSON.parse(evt.target?.result as string);
        if (!isValidWorkflow(json)) {
          setParseError(
            "This doesn't look like a ComfyUI API-format workflow. " + 'Each node needs a "class_type" and "inputs" field.',
          );
          setParsedWorkflow(null);
          setSummary(null);
          return;
        }

        setParsedWorkflow(json);
        setSummary(analyzeClientSide(json));

        if (!name) {
          setName(file.name.replace(/\.json$/i, ''));
        }
      } catch {
        setParseError('Failed to parse JSON file');
        setParsedWorkflow(null);
        setSummary(null);
      }
    };
    reader.readAsText(file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      setParseError('Please drop a JSON file');
      return;
    }

    processFile(file);
  };

  const handleUpload = async () => {
    if (!parsedWorkflow || !name.trim()) return;

    setUploading(true);
    setUploadError(null);
    try {
      const result = await uploadWorkflow(name.trim(), parsedWorkflow);
      navigate(`/admin/workflows/${result.id}`);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="h-full bg-gradient-to-br from-background via-background to-accent/30 dark:to-accent/10 p-8">
      <div className="max-w-2xl mx-auto">
        <header className="mb-8">
          <Link
            to="/admin/workflows"
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-3"
          >
            <ArrowLeft className="h-3 w-3" />
            Back to Workflows
          </Link>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Upload className="h-6 w-6" />
            New Workflow
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Upload a ComfyUI workflow JSON file (API format)</p>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>Upload Workflow</CardTitle>
            <CardDescription>Export your workflow from ComfyUI using "Save (API Format)" in Dev Mode</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="workflow-name">Workflow Name</Label>
              <Input
                id="workflow-name"
                placeholder="My Awesome Workflow"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Workflow File</Label>
              <input ref={fileInputRef} type="file" accept=".json" onChange={handleFileSelect} className="hidden" />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={
                  'w-full border-2 border-dashed rounded-lg p-8 ' +
                  'text-center transition-colors cursor-pointer ' +
                  (isDragging
                    ? 'border-primary bg-primary/10'
                    : parsedWorkflow
                      ? 'border-green-300 dark:border-green-500/40 bg-green-50/50 dark:bg-green-500/10'
                      : 'border-border bg-card hover:border-border/80 hover:bg-accent/50')
                }
              >
                {parsedWorkflow && summary ? (
                  <div className="space-y-2">
                    <CheckCircle className="h-8 w-8 text-green-500 dark:text-green-400 mx-auto" />
                    <p className="text-sm font-medium text-green-700 dark:text-green-400">{fileName}</p>
                    <p className="text-xs text-green-600 dark:text-green-400">
                      {summary.nodeCount} nodes &middot; {summary.inputCount} configurable inputs &middot;{' '}
                      {summary.classTypes.length} node types
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">Click to choose a different file</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <FileJson className="h-8 w-8 text-muted-foreground/60 mx-auto" />
                    <p className="text-sm font-medium text-foreground">
                      {isDragging ? 'Drop your file here' : 'Click to select or drag & drop a workflow JSON file'}
                    </p>
                    <p className="text-xs text-muted-foreground">ComfyUI API format (.json)</p>
                  </div>
                )}
              </button>
            </div>

            {parseError && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-2">
                <XCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                <p className="text-sm text-destructive">{parseError}</p>
              </div>
            )}

            {uploadError && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                <p className="text-sm text-destructive">{uploadError}</p>
              </div>
            )}

            <Button onClick={handleUpload} disabled={!parsedWorkflow || !name.trim() || uploading} className="w-full">
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading & Analyzing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload & Analyze
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
